import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { projectService } from '../services/projectService';
import { getInvitationErrorMessage } from '../utils/errorMessages';

/**
 * The `?invitation=<token>` half of an invitation link, shared by the signup
 * and login screens.
 *
 * Both need it: signup is where a new invitee lands, and login is the only
 * route left once an account exists — `authService.signUp` throws
 * `auth/email-already-in-use` before acceptance is ever attempted, so without
 * this on the login screen an invitee whose first acceptance failed could
 * never redeem their token at all.
 *
 * `acceptInvitation` resolves to `{ ok }` and never throws: a failure must not
 * fall through to the preview-code screen, which asks an invitee for a code
 * nobody issued them. Every check — expiry, cancelled, already used — stays
 * server-side in `acceptProjectInvitation`; this only calls it.
 */
export function useInvitationAcceptance() {
  const [searchParams] = useSearchParams();
  const [invitationToken, setInvitationToken] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [invitationError, setInvitationError] = useState('');

  useEffect(() => {
    const token = searchParams.get('invitation');
    if (token) setInvitationToken(token);
  }, [searchParams]);

  const acceptInvitation = useCallback(async () => {
    if (!invitationToken || accepting) return { ok: false };

    setAccepting(true);
    setInvitationError('');
    try {
      const result =
        await projectService.acceptProjectInvitation(invitationToken);
      if (result?.success) return { ok: true };
      setInvitationError(getInvitationErrorMessage({}));
      return { ok: false };
    } catch (error) {
      console.error('Failed to accept invitation:', error);
      setInvitationError(getInvitationErrorMessage(error));
      return { ok: false };
    } finally {
      setAccepting(false);
    }
  }, [invitationToken, accepting]);

  return { invitationToken, invitationError, accepting, acceptInvitation };
}
